import { useEffect, useRef } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Sms from "@mui/icons-material/Sms";
import HistoryIcon from "@mui/icons-material/History";
import InfoIcon from "@mui/icons-material/Info";
import CommentIcon from "@mui/icons-material/Comment";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopy from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import { convertToLocal, timeDiff, addSpacesToPhoneNumber } from "../../../common/utils/functions";
import { PositionStatus } from "../../../common/components/PositionStatus";
import { METER_TO_FEET_CONVERSION_RATE } from "../../../common/utils/constants";
import { StyledPaper, LimitedBackdrop } from "../../../common/utils/stylings";

const InfiniteSentinel = ({ hasMore, onLoadMore, rootRef }) => {
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!hasMore || !sentinelRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onLoadMore();
        }
      },
      { root: rootRef?.current || null, rootMargin: "120px", threshold: 0 },
    );

    observer.observe(sentinelRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, onLoadMore, rootRef]);

  if (!hasMore) {
    return null;
  }

  return <div ref={sentinelRef} style={{ height: 1, width: "100%" }} />;
};

const RequestMeta = ({ label, value }) => {
  return (
    <Typography component="div" variant="body2" sx={{ wordBreak: "break-word" }}>
      <strong>{label}: </strong>
      {value ?? "-"}
    </Typography>
  );
};

const ListWrapper = ({ isLoading, children, hasMore, onLoadMore }) => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef(null);
  const itemCount = Array.isArray(children) ? children.filter(Boolean).length : children ? 1 : 0;

  return (
    <StyledPaper
      ref={scrollContainerRef}
      sx={{
        mb: 1,
        p: 1,
        maxHeight: { xs: "40vh", sm: "42vh", md: "46vh" },
        overflowY: "auto",
        overflowX: "hidden",
        overscrollBehavior: "contain",
        position: "relative",
      }}
    >
      <LimitedBackdrop sx={{ backgroundColor: "#fffff", color: "#fffff", position: "absolute", zIndex: 1 }} open={isLoading}>
        <CircularProgress />
      </LimitedBackdrop>
      <Stack spacing={1}>
        {itemCount === 0 ? (
          <Typography variant="body2" sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
            {t("no_requests_to_show")}
          </Typography>
        ) : (
          children
        )}
        <InfiniteSentinel hasMore={hasMore} onLoadMore={onLoadMore} rootRef={scrollContainerRef} />
        <Typography variant="caption" sx={{ textAlign: "center", pb: 0.5, color: "text.secondary" }}>
          {isLoading ? t("loading_requests") : hasMore ? t("scroll_for_more_requests") : itemCount > 0 ? t("all_requests_loaded") : ""}
        </Typography>
      </Stack>
    </StyledPaper>
  );
};

const actionsSx = {
  flexWrap: "wrap",
  gap: 0.5,
  px: 1,
  pb: 1,
  "& .MuiButton-root": {
    minWidth: 0,
    px: { xs: 0.75, sm: 1 },
    fontSize: { xs: "0.68rem", sm: "0.75rem" },
  },
};

export const ActiveTable = ({
  handleShowComments,
  active,
  onHighlightActive,
  highlightActive,
  endRequest,
  setSelectedRequest,
  handleShowRequestInfo,
  setSelected,
  history,
  isLoading,
  handleOpenConfirm,
  hasMore,
  onLoadMore,
  mqttPositions = {},
}) => {
  const { t } = useTranslation();

  return (
    <ListWrapper isLoading={isLoading} hasMore={hasMore} onLoadMore={onLoadMore}>
      {active?.map((request) => {
        const isHighlighted = highlightActive && highlightActive.id === request.id;

        return (
          <Card key={request.id} variant="outlined" onClick={(e) => onHighlightActive(e, request)} sx={{ cursor: "pointer", backgroundColor: isHighlighted ? "action.selected" : "background.paper" }}>
            <CardContent>
              <Stack spacing={0.75}>
                <Typography variant="subtitle1" fontWeight={600}>
                  #{request.id} - {request.displayName} {request.comments?.length > 0 ? "*" : ""}
                </Typography>
                {request.prioUser && <Typography variant="body2">{t("prio_user")}</Typography>}
                <RequestMeta
                  label={t("pilot_position")}
                  value={
                    <PositionStatus status={mqttPositions?.[request.id] ? (mqttPositions[request.id].isInside ? "inside" : "outside") : "unknown"} timestamp={mqttPositions?.[request.id]?.timestamp} />
                  }
                />
                <RequestMeta label={t("phone")} value={request.phoneNumber ? addSpacesToPhoneNumber(request.phoneNumber) : "-"} />
                <RequestMeta label={t("address")} value={request.address} />
                <RequestMeta label={t("height")} value={`${request.height}m (${Math.ceil(request.height * METER_TO_FEET_CONVERSION_RATE)} ${t("feet")})`} />
                <RequestMeta label={t("time_active")} value={timeDiff(request.activatedDateByAts)} />
              </Stack>
            </CardContent>
            <CardActions sx={actionsSx}>
              <Button
                size="small"
                startIcon={<StopCircleIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  endRequest(request);
                }}
              >
                {t("end")}
              </Button>
              <Button
                size="small"
                startIcon={<Sms />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRequest(request);
                  handleOpenConfirm();
                }}
              >
                {t("cancel_immediately")}
              </Button>
              <Button
                size="small"
                startIcon={<InfoIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRequest(request);
                  handleShowRequestInfo();
                }}
              >
                {t("more_info")}
              </Button>
              <Button
                size="small"
                startIcon={<HistoryIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelected(request);
                  history.push("pilot-history");
                }}
              >
                {t("history")}
              </Button>
              <Button
                size="small"
                startIcon={<CommentIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRequest(request);
                  handleShowComments();
                }}
              >
                {t("comments")}
              </Button>
            </CardActions>
          </Card>
        );
      })}
    </ListWrapper>
  );
};

export const UpcomingTable = ({
  handleShowComments,
  upcoming,
  onHighlightUpcoming,
  highlightUpcoming,
  activateRequest,
  setSelectedRequest,
  handleShowRequestInfo,
  setSelected,
  history,
  isLoading,
  onErase,
  setHighlightUpcoming,
  hasMore,
  onLoadMore,
  mqttPositions = {},
}) => {
  const { t } = useTranslation();

  return (
    <ListWrapper isLoading={isLoading} hasMore={hasMore} onLoadMore={onLoadMore}>
      {upcoming?.map((request) => {
        const isHighlighted = highlightUpcoming && highlightUpcoming.id === request.id;

        return (
          <Card
            key={request.id}
            variant="outlined"
            onClick={(e) => onHighlightUpcoming(e, request)}
            sx={{ cursor: "pointer", backgroundColor: isHighlighted ? "action.selected" : "background.paper" }}
          >
            <CardContent>
              <Stack spacing={0.75}>
                <Typography variant="subtitle1" fontWeight={600}>
                  #{request.id} - {request.displayName} {request.comments?.length > 0 ? "*" : ""}
                </Typography>
                {request.prioUser && <Typography variant="body2">{t("prio_user")}</Typography>}
                <RequestMeta
                  label={t("pilot_position")}
                  value={
                    <PositionStatus status={mqttPositions?.[request.id] ? (mqttPositions[request.id].isInside ? "inside" : "outside") : "unknown"} timestamp={mqttPositions?.[request.id]?.timestamp} />
                  }
                />
                <RequestMeta label={t("phone")} value={request.phoneNumber ? addSpacesToPhoneNumber(request.phoneNumber) : "-"} />
                <RequestMeta label={t("address")} value={request.address} />
                <RequestMeta label={t("height")} value={`${request.height}m (${Math.ceil(request.height * METER_TO_FEET_CONVERSION_RATE)} ${t("feet")})`} />
                <RequestMeta label={t("planned_date")} value={convertToLocal(request.plannedDate)} />
              </Stack>
            </CardContent>
            <CardActions sx={actionsSx}>
              <Button
                size="small"
                startIcon={<PlayCircleFilledIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  activateRequest(request);
                  setHighlightUpcoming();
                }}
              >
                {t("activate")}
              </Button>
              <Button
                size="small"
                startIcon={<InfoIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRequest(request);
                  handleShowRequestInfo();
                }}
              >
                {t("more_info")}
              </Button>
              <Button
                size="small"
                startIcon={<HistoryIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelected(request);
                  history.push("pilot-history");
                }}
              >
                {t("history")}
              </Button>
              <Button
                size="small"
                startIcon={<CommentIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRequest(request);
                  handleShowComments();
                }}
              >
                {t("comments")}
              </Button>
              <Button
                size="small"
                startIcon={<DeleteIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  onErase(request);
                }}
              >
                {t("remove")}
              </Button>
            </CardActions>
          </Card>
        );
      })}
    </ListWrapper>
  );
};

export const EarlierTable = ({
  handleShowComments,
  earlier,
  onHighlightEarlier,
  highlightEarlier,
  setSelectedRequest,
  handleShowRequestInfo,
  setSelected,
  history,
  onPostCopied,
  isLoading,
  handleShowRequestEdit,
  hasMore,
  onLoadMore,
}) => {
  const { t } = useTranslation();

  return (
    <ListWrapper isLoading={isLoading} hasMore={hasMore} onLoadMore={onLoadMore}>
      {earlier?.map((request) => {
        const isHighlighted = highlightEarlier && highlightEarlier.id === request.id;

        return (
          <Card key={request.id} variant="outlined" onClick={(e) => onHighlightEarlier(e, request)} sx={{ cursor: "pointer", backgroundColor: isHighlighted ? "action.selected" : "background.paper" }}>
            <CardContent>
              <Stack spacing={0.75}>
                <Typography variant="subtitle1" fontWeight={600}>
                  #{request.id} - {request.displayName} {request.comments?.length > 0 ? "*" : ""}
                </Typography>
                {request.prioUser && <Typography variant="body2">{t("prio_user")}</Typography>}
                <RequestMeta label={t("address")} value={request.address} />
                <RequestMeta label={t("time_active")} value={request.flightTime} />
                <RequestMeta label={t("end_datetime")} value={`${convertToLocal(request.activatedDateByAts)} / ${convertToLocal(request.endedDateByAts)}`} />
              </Stack>
            </CardContent>
            <CardActions sx={actionsSx}>
              <Button
                size="small"
                startIcon={<ContentCopy />}
                onClick={(e) => {
                  e.stopPropagation();
                  onPostCopied(request);
                }}
              >
                {t("copy")}
              </Button>
              <Button
                size="small"
                startIcon={<InfoIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRequest(request);
                  handleShowRequestInfo();
                }}
              >
                {t("more_info")}
              </Button>
              <Button
                size="small"
                startIcon={<HistoryIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelected(request);
                  history.push("pilot-history");
                }}
              >
                {t("history")}
              </Button>
              <Button
                size="small"
                startIcon={<CommentIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRequest(request);
                  handleShowComments();
                }}
              >
                {t("comments")}
              </Button>
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRequest(request);
                  handleShowRequestEdit();
                }}
              >
                {t("edit_start_or_end_datetime")}
              </Button>
            </CardActions>
          </Card>
        );
      })}
    </ListWrapper>
  );
};

export const ErasedTable = ({
  handleShowComments,
  erased,
  onHighlightErased,
  highlightErased,
  onPostCopied,
  setSelectedRequest,
  handleShowRequestInfo,
  setSelected,
  history,
  isLoading,
  hasMore,
  onLoadMore,
}) => {
  const { t } = useTranslation();

  return (
    <ListWrapper isLoading={isLoading} hasMore={hasMore} onLoadMore={onLoadMore}>
      {erased?.map((request) => {
        const isHighlighted = highlightErased && highlightErased.id === request.id;

        return (
          <Card key={request.id} variant="outlined" onClick={(e) => onHighlightErased(e, request)} sx={{ cursor: "pointer", backgroundColor: isHighlighted ? "action.selected" : "background.paper" }}>
            <CardContent>
              <Stack spacing={0.75}>
                <Typography variant="subtitle1" fontWeight={600}>
                  #{request.id} - {request.displayName} {request.comments?.length > 0 ? "*" : ""}
                </Typography>
                {request.prioUser && <Typography variant="body2">{t("prio_user")}</Typography>}
                <RequestMeta label={t("phone")} value={request.phoneNumber ? addSpacesToPhoneNumber(request.phoneNumber) : "-"} />
                <RequestMeta label={t("address")} value={request.address} />
                <RequestMeta label={t("planned_date")} value={convertToLocal(request.plannedDate)} />
              </Stack>
            </CardContent>
            <CardActions sx={actionsSx}>
              <Button
                size="small"
                startIcon={<ContentCopy />}
                onClick={(e) => {
                  e.stopPropagation();
                  onPostCopied(request);
                }}
              >
                {t("copy")}
              </Button>
              <Button
                size="small"
                startIcon={<InfoIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRequest(request);
                  handleShowRequestInfo();
                }}
              >
                {t("more_info")}
              </Button>
              <Button
                size="small"
                startIcon={<HistoryIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelected(request);
                  history.push("pilot-history");
                }}
              >
                {t("history")}
              </Button>
              <Button
                size="small"
                startIcon={<CommentIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRequest(request);
                  handleShowComments();
                }}
              >
                {t("comments")}
              </Button>
            </CardActions>
          </Card>
        );
      })}
    </ListWrapper>
  );
};
